import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Col,
  Drawer,
  Flex,
  Form,
  Input,
  Pagination,
  Popconfirm,
  Popover,
  Radio,
  Row,
  Select,
  Steps,
  Table,
} from 'antd';
import { ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { collect } from 'collect.js';
import i18n from 'i18next';
import useHistory from '../../../../hooks/navigation/useHistory';
import useForm from '../../../../hooks/utility/useForm';
import {
  HELLOPRINT_SERVICE_LEVELS,
  PolygraphyProductFormat,
  PolygraphyProductUrlType,
} from '../../../../services/exports/Constants';
import useApiClient from '../../../../hooks/api/useApiClient';
import useFeedbackLabel from '../../../../hooks/ui/useFeedbackLabel';
import HelperMethods from '../../../../services/exports/HelperMethods';
import CardWrapper from '../../../../components/cards/CardWrapper';
import AddressFields from '../../../../components/form/address/AddressFields';
import useScreenType from '../../../../hooks/context/useScreenType';
import usePermissions from '../../../../hooks/auth/usePermissions';
import useHelpers from '../../../../hooks/context/useHelpers';
import { ScreenEnum } from '../../../../hooks/navigation/Screens';

export const ProductOrderingSteps = {
  ProductInfo: 'product_info',
  ShippingInfo: 'shipping_info',
};

const PricingTable = {
  [PolygraphyProductFormat.FlyerA5]: {
    25: 21.9,
    50: 24.9,
    75: 28.9,
    100: 31.9,
    150: 36.9,
    200: 41.9,
    250: 47.9,
    500: 52.9,
    1000: 59.9,
    2500: 84.9,
    5000: 124.95,
    10000: 192.95,
    15000: 249.95,
    20000: 279.95,
  },
  [PolygraphyProductFormat.FlyerA6]: {
    25: 29.0,
    50: 29.0,
    75: 29.0,
    100: 29.0,
    150: 34.0,
    200: 39.0,
    250: 44.0,
    500: 49.0,
    1000: 55.0,
    2500: 79.0,
    5000: 115.0,
    10000: 179.0,
    15000: 229.0,
    20000: 259.0,
  },
  [PolygraphyProductFormat.Sticker60x60]: {
    100: 30.0,
    200: 35.0,
    300: 37.0,
    500: 40.0,
    1000: 45.0,
    2000: 65.0,
    3000: 90.0,
    4000: 100.0,
    5000: 115.0,
    10000: 179.0,
    15000: 239.0,
    20000: 289.0,
  },
  [PolygraphyProductFormat.DoorStickerA5]: {
    1: 21.99,
    2: 24.99,
    3: 27.99,
    4: 31.99,
    5: 34.99,
  },
  [PolygraphyProductFormat.DoorSticker300x300]: {
    1: 21.99,
    2: 24.99,
    3: 27.99,
    4: 31.99,
    5: 34.99,
  },
};

function ProductInfoStep({ products, data, selectedProduct, update }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:Polygraphy:Orders' });

  const { isSuperAdmin } = usePermissions();
  const { getProductName } = useProductNames();

  const productOptions = useMemo(
    () =>
      products.map((item) => ({
        label: getProductName(item),
        value: item.id,
      })),
    [products, i18n.language],
  );

  const quantityOptions = useMemo(
    () =>
      Object.keys(PricingTable[selectedProduct?.format ?? PolygraphyProductFormat.FlyerA6]).map((item) => ({
        label: item,
        value: item,
      })),
    [selectedProduct?.format],
  );

  useEffect(() => {
    if (!quantityOptions.some((option) => String(option.value) === String(data.quantity))) {
      update('quantity', quantityOptions[0]?.value);
    }
  }, [quantityOptions, data.quantity, update]);

  return (
    <Form layout="vertical">
      <Row gutter={12}>
        <Col md={24}>
          <Form.Item label={t('form.polygraphy_product_id.label')}>
            <Flex>
              <Select
                value={data.polygraphy_product_id}
                placeholder={t('form.polygraphy_product_id.placeholder')}
                onChange={(value) => update('polygraphy_product_id', value)}
                options={productOptions}
              />
              {Boolean(selectedProduct?.file_url) && (
                <Button
                  type="link"
                  icon={<ArrowRightOutlined />}
                  onClick={() => window.open(selectedProduct.file_url, '_blank')}
                >
                  {t('buttons.preview')}
                </Button>
              )}
            </Flex>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('form.quantity.label')}>
            <Select
              value={data.quantity}
              placeholder={t('form.quantity.placeholder')}
              onChange={(value) => update('quantity', value)}
              options={quantityOptions}
            />
          </Form.Item>
        </Col>
        {isSuperAdmin && (
          <Col xs={24} md={12}>
            <Form.Item label={t('form.service_level.label')} className="!mb-none">
              <Select
                value={data.service_level}
                placeholder={t('form.service_level.placeholder')}
                onChange={(value) => update('service_level', value)}
                options={Object.values(HELLOPRINT_SERVICE_LEVELS).map((item) => ({
                  label: t(`form.service_level.options.${item}`),
                  value: item,
                }))}
                className="mb-md"
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
}

function ShippingInfoStep({ data, update }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:Polygraphy:Orders' });

  return (
    <Form layout="vertical">
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item label={t('form.shipping_company_name.label')}>
            <Input
              value={data.shipping_company_name}
              placeholder={t('form.shipping_company_name.placeholder')}
              onChange={(e) => update('shipping_company_name', e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('form.shipping_phone_number.label')}>
            <Input
              value={data.shipping_phone_number}
              placeholder={t('form.shipping_phone_number.placeholder')}
              onChange={(e) => update('shipping_phone_number', e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item label={t('form.shipping_salutation.label')}>
            <Select
              value={data.shipping_salutation}
              placeholder={t('form.shipping_salutation.placeholder')}
              onChange={(value) => update('shipping_salutation', value)}
              options={[t('form.shipping_salutation.options.mr'), t('form.shipping_salutation.options.mrs')].map(
                (item) => ({ label: item, value: item }),
              )}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('form.shipping_first_name.label')}>
            <Input
              value={data.shipping_first_name}
              placeholder={t('form.shipping_first_name.placeholder')}
              onChange={(e) => update('shipping_first_name', e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('form.shipping_last_name.label')}>
            <Input
              value={data.shipping_last_name}
              placeholder={t('form.shipping_last_name.placeholder')}
              onChange={(e) => update('shipping_last_name', e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>
      <AddressFields prefix="shipping" useGrid data={data} onChange={update} />
    </Form>
  );
}

function OrderConfirmationModal({ data, selectedProduct, onChange, onSuccess, onClose }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:Polygraphy:Orders' });

  const { PolygraphyManager } = useApiClient();

  const { isDesktop } = useScreenType();
  const { formatCurrency } = useHelpers();
  const { setErrorFeedback, renderFeedbackLabel, resetFeedback } = useFeedbackLabel();
  const { getProductName } = useProductNames();

  const [loading, setLoading] = useState(false);

  const price = useMemo(
    () => PricingTable[selectedProduct.format][data.quantity],
    [selectedProduct.format, data.quantity],
  );

  const createOrder = async () => {
    resetFeedback();
    setLoading(true);
    const { success, data: responseData } = await PolygraphyManager.createOrder(data);
    setLoading(false);

    if (!success) {
      setErrorFeedback(responseData);

      return toast.error(t('toasts.failed_to_create_order'));
    }

    onSuccess(responseData);
    onClose();

    return toast.success(t('toasts.order_got_created'));
  };

  return (
    <Drawer
      open
      size={window.innerWidth * (isDesktop ? 0.5 : 1)}
      title={t('modals.confirmation.title')}
      onClose={onClose}
    >
      {renderFeedbackLabel}
      {selectedProduct.data.url_type === PolygraphyProductUrlType.OnlineShop && (
        <Alert
          type="warning"
          showIcon
          className="mb-md"
          title={
            <Trans
              t={t}
              values={{
                url: selectedProduct.data.url,
              }}
              components={{
                url: <a href={selectedProduct.data.url} target="_blank" rel="noopener noreferrer" />,
              }}
            >
              feedback.shop_url_used
            </Trans>
          }
        />
      )}
      <h6 className="underline">{t(`steps.${ProductOrderingSteps.ProductInfo}`)}</h6>
      <ul>
        <li>
          <span>
            {t('form.polygraphy_product_id.label')}: {getProductName(selectedProduct)}
          </span>
          <Button
            type="link"
            icon={<ArrowRightOutlined />}
            onClick={() => window.open(selectedProduct.file_url, '_blank')}
            className="ml-md"
          >
            {t('buttons.preview')}
          </Button>
        </li>
        <li>
          {t('form.quantity.label')}: {data.quantity}
        </li>
        <li>
          {t('form.service_level.label')}: {t(`form.service_level.options.${data.service_level}`)}
        </li>
      </ul>
      <h6 className="underline">{t(`steps.${ProductOrderingSteps.ShippingInfo}`)}</h6>
      <ul>
        <li>
          {t('labels.shipping_company')}: {data.shipping_company_name}
        </li>
        <li>
          {t('labels.shipping_salutation')}: {data.shipping_salutation}
        </li>
        <li>
          {t('labels.shipping_first_name')}: {data.shipping_first_name}
        </li>
        <li>
          {t('labels.shipping_last_name')}: {data.shipping_last_name}
        </li>
        <li>
          {t('labels.shipping_address')}: {data.shipping_address}
        </li>
        <li>
          {t('labels.shipping_zip_code')}: {data.shipping_zip_code}
        </li>
        <li>
          {t('labels.shipping_city')}: {data.shipping_city}
        </li>
        <li>
          {t('labels.shipping_phone_number')}: {data.shipping_phone_number}
        </li>
      </ul>
      <Alert
        type="info"
        showIcon
        className="my-lg"
        title={
          <Trans t={t} values={{ amount: formatCurrency(price) }}>
            feedback.order_total_price
          </Trans>
        }
      />
      <h6 className="underline">{t('steps.payment')}</h6>
      <Radio.Group
        className="flex flex-col gap-xs mt-xs"
        value={data.create_invoice}
        onChange={(e) => onChange('create_invoice', e.target.value)}
        options={[
          { value: true, label: t('form.create_invoice.options.true') },
          { value: false, label: t('form.create_invoice.options.false') },
        ]}
      />
      <Flex justify="end">
        {data.create_invoice === false ? (
          <Popconfirm
            title={t('modals.free_order_confirmation.title', { product: getProductName(selectedProduct) })}
            description={t('modals.free_order_confirmation.description')}
            okButtonProps={{ danger: true, loading }}
            okText={t('modals.free_order_confirmation.buttons.confirm')}
            onConfirm={createOrder}
          >
            <Button type="primary">{t('buttons.confirm')}</Button>
          </Popconfirm>
        ) : (
          <Button type="primary" loading={loading} disabled={data.create_invoice === undefined} onClick={createOrder}>
            {t('buttons.confirm')}
          </Button>
        )}
      </Flex>
    </Drawer>
  );
}

export default function PolygraphyOrdersView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:Polygraphy:Orders' });

  const { PolygraphyManager } = useApiClient();
  const { company } = useSelector((store) => store.currentCompany);
  const { business_profile } = company;

  const history = useHistory();
  const { getProductName } = useProductNames();

  const [products, setProducts] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);

  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showOrderConfirmationPopup, setShowOrderConfirmationPopup] = useState(false);
  const toggleForm = () => setShowOrderForm((current) => !current);
  const toggleOrderConfirmationPopup = () => setShowOrderConfirmationPopup((current) => !current);

  const { data, update } = useForm({
    polygraphy_product_id: null,
    quantity: 1000,
    service_level: HELLOPRINT_SERVICE_LEVELS.SAVER,
    shipping_phone_number: business_profile?.shipping_phone_number ?? business_profile?.owner_phone_number,
    shipping_company_name: business_profile?.shipping_company_name ?? company?.name,
    shipping_salutation: business_profile?.shipping_salutation ?? t('form.shipping_salutation.options.mr'),
    shipping_first_name: business_profile?.shipping_first_name ?? business_profile?.first_name,
    shipping_last_name: business_profile?.shipping_last_name ?? business_profile?.owner_last_name,
    shipping_address: business_profile?.shipping_address ?? business_profile?.legal_address,
    shipping_city: business_profile?.shipping_city ?? business_profile?.legal_city,
    shipping_state: business_profile?.shipping_state ?? business_profile?.legal_state,
    shipping_country: business_profile?.shipping_country ?? business_profile?.legal_country,
    shipping_zip_code: business_profile?.shipping_zip_code ?? business_profile?.legal_zip_code,
    create_invoice: true,
  });
  const [loading, setLoading] = useState(false);

  const [orderHistory, setOrderHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  const selectedProduct = useMemo(
    () => collect(products).firstWhere('id', data.polygraphy_product_id),
    [products, data.polygraphy_product_id],
  );

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadOrderHistory(currentPage);
  }, [currentPage]);

  async function loadData() {
    setLoading(true);
    const { success, data: responseData } = await PolygraphyManager.getProducts();
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    return setProducts(responseData);
  }

  async function loadOrderHistory(page) {
    setLoading(true);
    const { success, data: responseData } = await PolygraphyManager.getOrders(page);
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setCurrentPage(responseData?.current_page);
    setTotal(responseData?.total);

    return setOrderHistory(responseData.data);
  }

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('table.fields.product'),
      key: 'product',
      render: (item) => (
        <span>
          {getProductName(item.polygraphy_product)}
          {Boolean(item.polygraphy_product.file_url) && (
            <Button
              type="link"
              icon={<ArrowRightOutlined />}
              onClick={() => window.open(item.polygraphy_product.file_url, '_blank')}
            >
              {t('buttons.preview')}
            </Button>
          )}
        </span>
      ),
    },
    {
      title: t('table.fields.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('table.fields.status'),
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: t('table.fields.service_level'),
      dataIndex: 'service_level',
      key: 'service_level',
    },
    {
      title: t('table.fields.shipping'),
      dataIndex: 'shipping',
      key: 'shipping',
      render: (_, item) => (
        <Popover
          content={
            <ul>
              <ul className="!p-inherit">
                <li>
                  {t('labels.shipping_company')}: {item.shipping_company_name}
                </li>
                <li>
                  {t('labels.shipping_salutation')}: {item.shipping_salutation}
                </li>
                <li>
                  {t('labels.shipping_first_name')}: {item.shipping_first_name}
                </li>
                <li>
                  {t('labels.shipping_last_name')}: {item.shipping_last_name}
                </li>
                <li>
                  {t('labels.shipping_address')}: {item.shipping_address}
                </li>
                <li>
                  {t('labels.shipping_zip_code')}: {item.shipping_zip_code}
                </li>
                <li>
                  {t('labels.shipping_city')}: {item.shipping_city}
                </li>
                <li>
                  {t('labels.shipping_phone_number')}: {item.shipping_phone_number}
                </li>
              </ul>
            </ul>
          }
        >
          <a href="#">{t('labels.show_shipping_details')}</a>
        </Popover>
      ),
    },
    {
      title: t('table.fields.tracking_url'),
      dataIndex: 'tracking_url',
      key: 'tracking_url',
      render: (text) =>
        text ? (
          <a href={text} target="_blank" rel="noopener noreferrer">
            {t('labels.track_order')}
          </a>
        ) : (
          t('labels.pending_from_provider')
        ),
    },
    {
      title: t('table.fields.created'),
      dataIndex: 'created_at',
      key: 'created',
      render: (text) => HelperMethods.formatDate(text),
    },
    {
      title: t('table.fields.updated'),
      dataIndex: 'updated_at',
      key: 'updated',
      render: (text) => HelperMethods.formatDate(text),
    },
  ];

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => history.push(ScreenEnum.MarketingPolygraphyProducts)}
      >
        {t('buttons.back_to_templates')}
      </Button>
      <h2 className="mb-md">{t('title')}</h2>
      <CardWrapper>
        <div className="flex justify-between">
          <h6 className="my-auto underline">{t('form.title')}</h6>
          <Button
            onClick={toggleForm}
            className={classnames('cursor-pointer hover:bg-gray-200 p-2 rounded-md', { 'rotate-90': !showOrderForm })}
          >
            <ArrowDownOutlined />
          </Button>
        </div>
        {showOrderForm && (
          <div>
            <Steps
              className="my-md px-md max-w-[70%] mx-auto"
              current={currentStep}
              onChange={setCurrentStep}
              items={[
                {
                  title: t(`steps.${ProductOrderingSteps.ProductInfo}`),
                },
                {
                  title: t(`steps.${ProductOrderingSteps.ShippingInfo}`),
                },
              ]}
            />
            {currentStep === 0 && (
              <>
                <ProductInfoStep products={products} data={data} selectedProduct={selectedProduct} update={update} />
                <Flex justify="end" className="mt-md">
                  <Button disabled={!selectedProduct} onClick={() => setCurrentStep(1)}>
                    {t('buttons.next')}
                  </Button>
                </Flex>
              </>
            )}
            {currentStep === 1 && (
              <>
                <ShippingInfoStep data={data} update={update} />
                <Flex justify="end" className="mt-md">
                  <Button type="primary" disabled={!selectedProduct} onClick={toggleOrderConfirmationPopup}>
                    {t('buttons.order')}
                  </Button>
                </Flex>
              </>
            )}
          </div>
        )}
      </CardWrapper>
      <div className="mt-6">
        <h2 className="mb-4">{t('table.title')}</h2>
        <Table
          columns={columns}
          className="mb-md rounded-xl shadow-card bg-white overflow-hidden"
          dataSource={orderHistory}
          rowKey="id"
          pagination={false}
          loading={loading}
        />
        <Pagination current={currentPage} defaultCurrent={currentPage} total={total} onChange={setCurrentPage} />
      </div>
      {showOrderConfirmationPopup && (
        <OrderConfirmationModal
          data={data}
          selectedProduct={selectedProduct}
          onChange={update}
          onClose={toggleOrderConfirmationPopup}
          onSuccess={(order) => setOrderHistory([order, ...orderHistory])}
        />
      )}
    </div>
  );
}

const useProductNames = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:Polygraphy:Orders' });

  const getProductName = (product) =>
    product.name ??
    t(`form.polygraphy_product_id.options.${product.type}.name`, {
      template: t(`form.polygraphy_product_id.options.${product.type}.templates.${product.template}`),
    });

  return { getProductName };
};
